import {useMe} from '@/features/authentication/queries/useMe'
import {useRootStore} from '@/store'
import {selectGlobalTranslateStore} from '@/store/selectors'
import {APP_LANGUAGES, FALLBACK_LANGUAGE} from '@/translations/i18n'
import {uniqueId} from 'lodash'
import {FC, useEffect, useLayoutEffect, useState} from 'react'
import {SwitchLanguage} from '../translate-switch/TranslateSwitch'
import {DotsLoader} from '@/components/ui/dotsLoader/DotsLoader'
import {useTranslation} from 'react-i18next'
import {EMOJI_REGEX} from '@/utilities/constants/chat'
import {MUTATION_KEYS} from '../../queries/keys'
import {useIsMutating} from '@tanstack/react-query'

// This component is a shell for the text. It uses the current translation settings
// in useGlobalTranslate. That is, if global translation is enabled, this component
// will understand what language the text is written in and translate it to the selected
// one in the global repository and show it.

// In case you need to bind not to the global language, but to the language that is selected
// as the main language for the application, you must use prop(isUseMainAppLanguage).
// onGetNativeLanguageOfText is a function that returns the juice of the language in which the text thrown into the content is written

type TranslateProps = {
    content: string
    isUseMainAppLanguage?: boolean
    onGetNativeLanguageOfText?: (language: string) => void
    nativeLanguage?: string
}
export const Translate: FC<TranslateProps> = ({
    content,
    onGetNativeLanguageOfText,
    isUseMainAppLanguage = false,
    nativeLanguage
}) => {
    const {
        i18n: {resolvedLanguage}
    } = useTranslation()
    const [localTranslateId, setLocalTranslateId] = useState('0')
    const {data: user} = useMe()
    const {currentLanguage, translateToggle, addRequest, clearResults} = useRootStore(selectGlobalTranslateStore)
    const activeTranslationMutations = useIsMutating({
        mutationKey: [MUTATION_KEYS.translation]
    })
    const isTranslating = Boolean(activeTranslationMutations > 0)
    const languageOfText = nativeLanguage?.toLowerCase()
    const defaultLanguage: SwitchLanguage = APP_LANGUAGES.reduce(
        (acc, item) =>
            item.value.toUpperCase() === (resolvedLanguage?.toUpperCase() || FALLBACK_LANGUAGE.toUpperCase())
                ? {code: item.value.toUpperCase(), name: item.label}
                : acc,
        {
            code: '',
            name: ''
        }
    )

    const spokenLanguages = user?.spokenLanguages || []
    const hasOnlyEmojis = EMOJI_REGEX.test(content)

    const isTranslate =
        currentLanguage &&
        !hasOnlyEmojis &&
        spokenLanguages?.findIndex(({code}) => code.toLowerCase() === languageOfText) === -1
    const selectedLanguageCode = isUseMainAppLanguage
        ? String(defaultLanguage?.code).toLowerCase()
        : String(currentLanguage?.code).toLowerCase()

    const translate = useRootStore(store => store.globalTranslate.results[localTranslateId])
    useLayoutEffect(() => {
        if (translateToggle && isTranslate && selectedLanguageCode && !translate) {
            const id = uniqueId()
            setLocalTranslateId(id)
            content && addRequest({id, text: content, targetLang: selectedLanguageCode})
        }
    }, [selectedLanguageCode, translateToggle, translate, content, isUseMainAppLanguage ? resolvedLanguage : null])
    useEffect(
        () => {
            if (!nativeLanguage && languageOfText && onGetNativeLanguageOfText)
                onGetNativeLanguageOfText?.(languageOfText.toLowerCase())
        },
        nativeLanguage ? [] : [languageOfText]
    )
    useEffect(() => {
        if (isUseMainAppLanguage) clearResults()
    }, [isUseMainAppLanguage ? resolvedLanguage : null])

    if (isTranslating) return <DotsLoader />
    return <>{translateToggle && isTranslate && translate ? translate.text : content}</>
}
